import { useEffect } from 'react';
import { AppShell, Burger, Group, Menu, ScrollArea, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowsExchange,
  IconBuilding,
  IconCalculator,
  IconChevronDown,
  IconFileCheck,
  IconFileInvoice,
  IconHome,
  IconId,
  IconListDetails,
  IconLogout,
  IconPackage,
  IconUsers,
  IconWritingSign,
} from '@tabler/icons-react';
import { NavLink as RouterNavLink, Outlet, useLocation } from 'react-router-dom';
import { ROLE_LABELS, useAuth } from '../auth/AuthContext';

interface NavItem {
  label: string;
  path: string;
  icon: typeof IconHome;
  /** Раздел ещё не реализован — показываем, но не даём кликнуть */
  disabled?: boolean;
}

const NAV_SECTIONS: Array<{ title: string; items: NavItem[] }> = [
  {
    title: 'Документы',
    items: [
      { label: 'Счета на оплату', path: '/invoices', icon: IconFileInvoice },
      { label: 'Документооборот', path: '/upd', icon: IconArrowsExchange },
      { label: 'Акты выполненных работ', path: '/acts', icon: IconFileCheck },
      { label: 'Сметы', path: '/estimates', icon: IconCalculator },
      { label: 'Доверенности', path: '/powers-of-attorney', icon: IconWritingSign },
    ],
  },
  {
    title: 'Справочники',
    items: [
      { label: 'Организации', path: '/organizations', icon: IconBuilding },
      { label: 'Контрагенты', path: '/counterparties', icon: IconUsers },
      { label: 'Номенклатура', path: '/nomenclature', icon: IconPackage },
      { label: 'Расценки', path: '/price-list', icon: IconListDetails },
      { label: 'Сотрудники', path: '/employees', icon: IconId },
    ],
  },
];

/** Двузначный номер раздела реестра: 01 «Главная», далее по разделам сквозной нумерацией. */
function useSectionNumbers(): Map<string, string> {
  const numbers = new Map<string, string>();
  let n = 1;
  numbers.set('/', String(n++).padStart(2, '0'));
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      numbers.set(item.path, String(n++).padStart(2, '0'));
    }
  }
  return numbers;
}

function RegistryNavRow({
  number,
  label,
  icon: Icon,
  active,
  disabled,
  to,
  onClick,
}: {
  number: string;
  label: string;
  icon: typeof IconHome;
  active: boolean;
  disabled?: boolean;
  to?: string;
  onClick?: () => void;
}) {
  const content = (
    <Group
      gap={0}
      wrap="nowrap"
      bg={active ? 'seal.0' : 'transparent'}
      style={{ opacity: disabled ? 0.45 : 1 }}
      py={7}
      pl={9}
      pr="xs"
    >
      <Text
        className="erp-tabular"
        size="xs"
        c={active ? 'seal.6' : 'ledger.5'}
        fw={active ? 700 : 500}
        w={22}
        style={{ flexShrink: 0 }}
      >
        {number}
      </Text>
      <Icon size={16} stroke={1.75} style={{ flexShrink: 0, marginRight: 10 }} />
      <Text size="sm" fw={active ? 600 : 400} c={active ? 'ledger.9' : 'ledger.7'} truncate>
        {label}
      </Text>
      {disabled && (
        <Text size="xs" c="ledger.4" ml="auto" style={{ flexShrink: 0 }}>
          скоро
        </Text>
      )}
    </Group>
  );

  if (disabled || !to) {
    return <div>{content}</div>;
  }

  return (
    <UnstyledButton component={RouterNavLink} to={to} onClick={onClick} display="block" w="100%">
      {content}
    </UnstyledButton>
  );
}

export function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure();
  const { user, logout } = useAuth();
  const location = useLocation();
  const sectionNumbers = useSectionNumbers();

  // Прокрутку документа блокируем только под каркасом приложения: печатные
  // бланки и экран входа монтируются вне него и должны прокручиваться обычно.
  useEffect(() => {
    document.body.classList.add('erp-app-shell');
    return () => document.body.classList.remove('erp-app-shell');
  }, []);

  return (
    <>
      {/*
      THESIS: Каждый документ — подшитый лист общего дела, а не карточка в дашборде; навигация читается как содержание реестра, не как меню.
      OWN-WORLD: Бумажный фон, чернильно-графитовая шкала (ledger), сургучно-красный акцент (seal) только в статусах и активном разделе; нулевые радиусы, линии разметки вместо теней, моноширинные номера/суммы (IBM Plex Mono).
      STORY: Бухгалтер/менеджер видит реестр разделов со сквозной нумерацией, находит нужный документ по номеру и статусу, не отвлекаясь на декор.
      FIRST VIEWPORT: Шапка-корешок реестра (тёмный ledger, название системы), слева — нумерованное содержание по разделам, справа — рабочая область на бумажном фоне.
      FORM: «Реестр сшитых и пронумерованных документов», направление 7/7 (seed key 0b05c54b).
      FINISH: unreviewed and undocumented is unfinished; this build ends with the finish review, the verdict, and DESIGN.md
      */}
      {/*
      Шапка и навигация закреплены и при прокрутке остаются на месте: прокручивается
      только список документов внутри рабочей области. Обложка реестра и его
      оглавление — часть переплёта, они не «уезжают» вместе с листами дела.
      */}
      <AppShell
      header={{ height: 52, offset: true }}
      navbar={{ width: 264, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header className="erp-cover">
        <Group h="100%" px="md" justify="space-between" wrap="nowrap" style={{ position: 'relative' }}>
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" color="var(--mantine-color-leather-1)" />
            <Text
              fw={700}
              size="sm"
              c="leather.0"
              tt="uppercase"
              style={{ letterSpacing: '0.06em' }}
            >
              ERP-complete
            </Text>
            <Text size="xs" c="leather.1" visibleFrom="sm">
              · реестр документов
            </Text>
          </Group>

          <Menu shadow="md" width={220} position="bottom-end">
            <Menu.Target>
              <UnstyledButton>
                <Group gap={8} wrap="nowrap">
                  <div style={{ textAlign: 'right' }}>
                    <Text size="sm" fw={500} lh={1.2} c="leather.0">
                      {user?.fullName}
                    </Text>
                    <Text size="xs" c="leather.1" lh={1.2}>
                      {user ? ROLE_LABELS[user.role] : ''}
                    </Text>
                  </div>
                  <IconChevronDown size={16} color="var(--mantine-color-leather-1)" />
                </Group>
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>{user?.email}</Menu.Label>
              <Menu.Divider />
              <Menu.Item
                color="seal"
                leftSection={<IconLogout size={16} />}
                onClick={() => void logout()}
              >
                Выйти
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar
        style={{ borderRight: '1px solid var(--mantine-color-ledger-2)' }}
        bg="var(--erp-paper)"
      >
        <AppShell.Section grow component={ScrollArea}>
          <RegistryNavRow
            number={sectionNumbers.get('/') ?? '01'}
            label="Главная"
            icon={IconHome}
            active={location.pathname === '/'}
            to="/"
            onClick={close}
          />

          {NAV_SECTIONS.map((section) => (
            <div key={section.title}>
              <Group
                gap={0}
                pl={9}
                pr="xs"
                mt="md"
                mb={2}
                style={{ borderBottom: '1px solid var(--erp-rule)' }}
                pb={4}
              >
                <Text size="xs" fw={600} c="ledger.5" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
                  {section.title}
                </Text>
              </Group>
              {section.items.map((item) => (
                <RegistryNavRow
                  key={item.path}
                  number={sectionNumbers.get(item.path) ?? '00'}
                  label={item.label}
                  icon={item.icon}
                  active={location.pathname.startsWith(item.path)}
                  disabled={item.disabled}
                  to={item.disabled ? undefined : item.path}
                  onClick={close}
                />
              ))}
            </div>
          ))}
        </AppShell.Section>
      </AppShell.Navbar>

      {/* Рабочая область ограничена высотой вьюпорта: страница целиком не
          прокручивается, за прокрутку отвечает содержимое раздела. */}
      <AppShell.Main bg="var(--erp-paper)" className="erp-main">
        <Outlet />
      </AppShell.Main>
      </AppShell>
    </>
  );
}
